<template>
    <main-layout-content :breadcrumbs="pageBreadcrumbs">
        <template #right-side>
            <common-button
                type="primary"
                size="tiny-micro"
                :label="$t('pages.company.my_company.breadcrumbs.preview')"
                @click="companyPreview"
            ></common-button>
        </template>
        <template #content>
            <common-tabs v-if="isReady && tabs.length" ref="tabs" :tabs="tabs" :activeTab="activeTab" @tab-change="tabChanged">
                <template #profile>
                    <div v-if="!company.deletedAt && isAdmin" class="my-company__deactivate" @click="toggleCompanyStatusModal">
                        {{ companyStatusHeading }}
                    </div>
                    <common-buttons v-if="company.deletedAt" class="my-company__buttons-activate">
                        <common-button
                            type="primary"
                            class="my-company__buttons-activate__button"
                            :label="$t('pages.company.my_company.reactivate_company')"
                            @click="toggleCompanyStatusModal"
                        ></common-button>
                    </common-buttons>
                    <company-private-profile
                        :company="company"
                        :isUser="isUser"
                        @reload-company="getCompany"
                        @go-to-gallery-tab="$refs.tabs.tabClicked('gallery')"
                    ></company-private-profile>
                    <common-content-wrapper width="medium">
                        <common-content-spacer :isCenter="true" height="medium">
                            <update-company-form
                                v-if="company?.id"
                                ref="companyForm"
                                :company="company"
                                :isUser="isUser"
                                @reload-company="getCompany"
                            ></update-company-form>
                        </common-content-spacer>
                    </common-content-wrapper>
                </template>
                <template #settings>
                    <update-company-package-form
                        :company="company"
                        @company-settings-updated="companySettingsUpdated"
                    ></update-company-package-form>
                </template>
                <template #gallery>
                    <multiple-image-upload
                        :photos="
                            company.photos.filter(
                                (item) => item.type === PhotoTypeEnum.COMPANY_OTHER || item.type === PhotoTypeEnum.COMPANY_COVER,
                            )
                        "
                        :uploadAPI="uploadPhotosApi"
                        :mainImageType="mainImageType"
                        @remove-photo="removePhoto($event)"
                        @set-main-photo="setCoverPhoto($event)"
                        @load-photos="loadData()"
                    ></multiple-image-upload>
                </template>
                <template #locations>
                    <update-locations-component
                        v-if="company?.cityId"
                        ref="updateLocationsComponent"
                        :isSubmittingForm="isProcessingLocationForm"
                        :locations="company.locationsForCompany"
                        :isCompany="true"
                        :company="company"
                        :isUser="isUser"
                        @add-location="formSubmit"
                        @location-deleted="locationDeleted"
                        @edit-location="editFormSubmit"
                    ></update-locations-component>
                </template>
                <template #services>
                    <common-content-spacer height="medium">
                        <common-buttons align="right">
                            <common-button
                                v-if="!isUser"
                                type="primary"
                                size="tiny-micro"
                                :isSubmittable="false"
                                :label="$t('layouts.app.top_menu.create_service')"
                                @click="toggleSelectLocationModal()"
                            ></common-button>
                        </common-buttons>

                        <offer-list-component
                            :offers="offers"
                            :companyId="company.id"
                            :allowRedirection="!isUser"
                            @offer-deleted="offerDeleted($event)"
                        ></offer-list-component>
                    </common-content-spacer>
                </template>
                <template #users>
                    <common-content-spacer height="medium">
                        <common-buttons align="right">
                            <common-button
                                type="link"
                                :isSubmittable="false"
                                :label="$t('forms.company.invite_company_user.buttons.invite_company_user')"
                                @click="openInviteUserModal()"
                            ></common-button>
                        </common-buttons>
                        <common-grid-items class="company-users-grid">
                            <user-card
                                v-for="companyUser in company.companyUsers"
                                :key="companyUser.user.id"
                                :companyUser="companyUser"
                                :companyId="company.id"
                                :showOnPublicFlag="true"
                                @company-show-user-changed="companyShowUserChanged($event, companyUser.companyRole)"
                                @company-show-public-user-changed="companyShowPublicUserChanged($event, companyUser.companyRole)"
                                @company-role-changed="companyRoleChanged($event, companyUser.user.id)"
                                @company-user-deleted="companyUserDeleted(companyUser.user.id)"
                            ></user-card>
                        </common-grid-items>
                    </common-content-spacer>
                </template>
                <template #clients>
                    <clients-list :company="company"></clients-list>
                </template>
                <template #inactivity>
                    <common-content-spacer height="medium">
                        <common-buttons align="right">
                            <common-button
                                v-if="companyAvailability"
                                type="primary"
                                size="tiny-micro"
                                :isSubmittable="false"
                                :label="$t('pages.company.my_company.buttons.add_inactivity')"
                                @click="openInactivityModal()"
                            ></common-button>
                        </common-buttons>
                        <inactive-availability-list
                            v-if="inactiveAvailabilities.length"
                            :inactiveAvailabilities="inactiveAvailabilities"
                            @inactivity-removed="getInactiveAvailabilities()"
                        >
                            <common-buttons v-if="inactiveAvailabilities?.length === totalCount">
                                <common-button
                                    type="primary"
                                    :label="$t('common.microcopy.show_more')"
                                    :isProcessing="isLoading"
                                    @click="loadMore"
                                ></common-button>
                            </common-buttons>
                        </inactive-availability-list>

                        <common-content-spacer v-else :isCenter="true" height="medium">
                            <p>{{ $t('pages.company.my_company.no_added_inactivity') }}</p>
                        </common-content-spacer>
                    </common-content-spacer>
                </template>
                <template #discounts>
                    <offer-discount :offers="offers" :companyId="company.id"></offer-discount>
                </template>
                <template #reminder>
                    <custom-reminder-component
                        ref="updateLocationsComponent"
                        :customReminders="customReminders"
                        :company="company"
                        :offers="offers"
                        :showPriceRSD="showPriceRSD"
                        @custom-reminder-changed="reminderChanged"
                    ></custom-reminder-component>
                </template>
                <template #chart>
                    <common-content-spacer height="medium">
                        <chart-filter :customReminders="customReminders" @chart-filter-change="chartFilterChanged"></chart-filter>
                        <chart :chartData="chartData" :totalSumLabel="totalSumLabel"></chart>
                    </common-content-spacer>
                </template>
                <template #invoices>
                    <common-content-spacer v-if="invoices.length" height="medium">
                        <invoice-list :invoices="invoices"></invoice-list>
                    </common-content-spacer>
                    <common-content-spacer v-else :isCenter="true" height="medium">
                        <p>{{ $t('pages.company.my_company.no_added_invoice') }}</p>
                    </common-content-spacer>
                </template>
            </common-tabs>
            <invite-company-user-modal
                v-if="isInviteCompanyUserModalOpen"
                :companyId="company.id"
                @close="closeInviteUserModal()"
                @company-user-invited="userInvitedSuccessfully()"
            ></invite-company-user-modal>
            <add-inactivity-modal
                v-if="isAddInactivityModalOpen"
                :availability="companyAvailability"
                @close="closeInactivityModal()"
                @out-if-avilability="outOfAvilabilityEvent($event)"
            ></add-inactivity-modal>
            <out-of-availability-modal
                v-if="isOutOfAvailabilityModalOpen"
                :offers="offersOutOfAvailability"
                @close="closeOutOfAvailabilityModal"
                @accepted="submitInactivityFormData()"
            ></out-of-availability-modal>
            <common-yes-no-modal
                v-if="isLocationYesNoModalOpen"
                height="tiny"
                :showWarningIcon="true"
                :headingText="yesNoHeaderText"
                :noButtonText="$t('common.microcopy.false')"
                :yesButtonText="$t('common.microcopy.true')"
                @close="isLocationYesNoModalOpen = false"
                @continue="openLocationTab"
            ></common-yes-no-modal>
            <select-location-modal
                v-if="isSelectLocationModalOpen"
                :locations="company.locationsForCompany.filter((location) => location.locationType === LocationTypeEnum.BUSINESS)"
                @close="toggleSelectLocationModal"
                @location-selected="createOffer($event)"
                @open-location-tab="openLocationTab()"
            ></select-location-modal>
            <common-yes-no-modal
                v-if="isChangeCompanyStatusModalOpen"
                :showWarningIcon="true"
                :headingText="companyStatusModalHeading"
                :noButtonText="$t('common.microcopy.false')"
                :yesButtonText="$t('common.microcopy.true')"
                height="tiny"
                @close="toggleCompanyStatusModal"
                @continue="changeCompanyStatus"
            ></common-yes-no-modal>
        </template>
    </main-layout-content>
</template>

<script>
    import RoutesEnum from 'enums/routes.enum'
    import CompanyAPI from 'api/CompanyAPI'
    import OfferAPI from 'api/OfferAPI'
    import MainLayoutContent from 'components/layouts/layout-content/MainLayoutContent'
    import CompanyPrivateProfile from 'components/page-elements/company/profile/CompanyPrivateProfile'
    import UpdateCompanyForm from 'components/forms/company/UpdateCompanyForm'
    import UpdateLocationsComponent from 'components/page-elements/locations/UpdateLocationsComponent'
    import OfferListComponent from 'components/page-elements/offer/OfferListComponent'
    import InviteCompanyUserModal from 'components/modals/company/InviteCompanyUserModal'
    import MultipleImageUpload from 'components/page-elements/images/MultipleImageUpload'
    import UserCard from 'components/page-elements/user/UserCard'
    import PhotoTypeEnum from 'enums/photo_type.enum'
    import CompanyRoles from 'enums/company_roles.enum'
    import AddInactivityModal from 'components/modals/company/AddInactivityModal'
    import AvailabilityAPI from 'api/AvailabilityAPI'
    import OutOfAvailabilityModal from 'components/modals/availability/OutOfAvailabilityModal'
    import InactiveAvailabilityList from 'components/page-elements/availability/InactiveAvailabilityList'
    import InvoiceList from 'components/page-elements/invoice/InvoiceList'
    import ClientsList from 'components/page-elements/clients/ClientsList'
    import LocationsAPI from 'api/LocationsAPI'
    import SelectLocationModal from 'components/modals/offer/SelectLocationModal'
    import UsersAPI from 'api/UsersAPI'
    import ErrorsEnum from 'enums/errors.enum'
    import OfferDiscount from 'components/page-elements/offer/OfferDiscount.vue'
    import UpdateCompanyPackageForm from 'components/forms/company/UpdateCompanyPackageForm'
    import Store from '~/store'
    import LocationTypeEnum from 'enums/location_type.enum'
    import ChartFilter from 'components/page-elements/user/ChartFilter.vue'
    import Chart from 'components/page-elements/chart/Chart.vue'
    import StatisticOptionsEnum from 'enums/statistic_options.enum'
    import CurrencyEnum from 'enums/currency.enum'
    import LanguageEnum from 'enums/language.enum'
    import PriceMixin from 'mixins/PriceMixin'
    import dayjs from 'dayjs'
    import ReservationStatusFilterEnum from 'enums/reservation_status_filter.enum'
    import CustomReminderComponent from 'components/page-elements/custom-reminder/CustomReminderComponent'
    import CountryBackendEnum from 'enums/country_backend.enum'

    export default {
        mixins: [PriceMixin],

        components: {
            MainLayoutContent,
            CompanyPrivateProfile,
            UpdateLocationsComponent,
            OfferListComponent,
            InviteCompanyUserModal,
            UserCard,
            UpdateCompanyForm,
            MultipleImageUpload,
            AddInactivityModal,
            OutOfAvailabilityModal,
            InactiveAvailabilityList,
            InvoiceList,
            ClientsList,
            SelectLocationModal,
            OfferDiscount,
            UpdateCompanyPackageForm,
            ChartFilter,
            Chart,
            CustomReminderComponent,
        },

        data: () => ({
            isReady: false,
            isProcessingLocationForm: false,
            company: {},
            offers: [],
            isInviteCompanyUserModalOpen: false,
            isAddInactivityModalOpen: false,
            userCompanyRole: null,
            activeTab: null,
            tabs: [],
            companyAvailability: [],
            offersOutOfAvailability: [],
            isOutOfAvailabilityModalOpen: false,
            dateFrom: undefined,
            dateTo: undefined,
            availabilityIds: [],
            inactiveAvailabilities: [],
            limit: 10,
            offset: 0,
            totalCount: 10,
            isLocationYesNoModalOpen: false,
            yesNoHeaderText: '',
            PhotoTypeEnum: PhotoTypeEnum,
            invoices: [],
            isSelectLocationModalOpen: false,
            isChangeCompanyStatusModalOpen: false,
            isLocationReminderCalled: false,
            LocationTypeEnum: LocationTypeEnum,
            chartQuery: {},
            currentYear: new Date().getFullYear(),
            chartData: [],
            totalSumLabel: undefined,
            statisticOption: StatisticOptionsEnum.OFFER,
            customReminders: [],
        }),

        computed: {
            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.company.my_company.breadcrumbs.my_company'),
                        route: { name: RoutesEnum.COMPANY.MY_COMPANIES },
                    },
                    {
                        label: this.company.name,
                    },
                ]
            },

            mainImageType() {
                return PhotoTypeEnum.COMPANY_COVER
            },

            isUser() {
                return this.userCompanyRole === CompanyRoles.COMPANY_USER.VALUE
            },

            isAdmin() {
                return this.userCompanyRole === CompanyRoles.COMPANY_ADMIN.VALUE
            },

            companyStatusHeading() {
                return this.company.deletedAt
                    ? this.$t('pages.company.my_company.reactivate_company')
                    : this.$t('pages.company.my_company.delete_company')
            },

            companyStatusModalHeading() {
                return this.company.deletedAt
                    ? this.$t('pages.company.my_company.activate_company_modal_heading')
                    : this.$t('pages.company.my_company.delete_company_modal_heading')
            },
            statusOptions() {
                return Object.values(ReservationStatusFilterEnum).map((status) => ({
                    label: this.$t(`components.reservations.reservation_status.statuses.${status.label}`),
                    status,
                    value: status.value,
                }))
            },
            isStatisticCustomReservationPage() {
                return this.statisticOption === StatisticOptionsEnum.CUSTOM_RESERVATION_PRICE
            },
            showPriceRSD() {
                return this.company?.city?.country?.id === CountryBackendEnum.SERBIA.id
            },
        },

        methods: {
            getTabs() {
                const currentCompany = this.currentCompany()
                this.tabs = currentCompany?.deletedAt
                    ? [
                          {
                              name: 'profile',
                              displayName: this.$t('pages.company.my_company.tabs.profile'),
                              icon: 'profile',
                              withPadding: true,
                          },
                          {
                              name: 'invoices',
                              displayName: this.$t('pages.company.my_company.tabs.invoices'),
                              icon: 'invoice',
                              withPadding: true,
                          },
                      ]
                    : [
                          {
                              name: 'profile',
                              displayName: this.$t('pages.company.my_company.tabs.profile'),
                              icon: 'profile',
                              withPadding: true,
                          },
                          {
                              name: 'settings',
                              displayName: this.$t('pages.company.my_company.tabs.settings'),
                              icon: 'settings',
                              withPadding: true,
                              hidden: this.isUser,
                          },
                          {
                              name: 'gallery',
                              displayName: this.$t('pages.company.my_company.tabs.gallery'),
                              icon: 'gallery',
                              withPadding: true,
                              hidden: this.isUser,
                          },
                          {
                              name: 'locations',
                              displayName: this.$t('pages.company.my_company.tabs.locations'),
                              icon: 'location',
                              withPadding: true,
                          },
                          {
                              name: 'services',
                              displayName: this.$t('pages.company.my_company.tabs.services'),
                              icon: 'services',
                              withPadding: true,
                          },
                          {
                              name: 'users',
                              displayName: this.$t('pages.company.my_company.tabs.users'),
                              icon: 'users',
                              withPadding: true,
                              hidden: this.isUser,
                          },
                          {
                              name: 'clients',
                              displayName: this.$t('pages.company.my_company.tabs.clients'),
                              icon: 'clients',
                              withPadding: true,
                              hidden: this.isUser,
                          },
                          {
                              name: 'inactivity',
                              displayName: this.$t('pages.company.my_company.tabs.inactivity'),
                              icon: 'clock',
                              withPadding: true,
                              hidden: this.offers.length === 0,
                          },
                          {
                              name: 'discounts',
                              displayName: this.$t('pages.company.my_company.tabs.discount'),
                              icon: 'discount',
                              withPadding: true,
                              hidden: this.offers.length === 0 || this.isUser,
                          },
                          {
                              name: 'invoices',
                              displayName: this.$t('pages.company.my_company.tabs.invoices'),
                              icon: 'invoice',
                              withPadding: true,
                              hidden: this.isUser,
                          },
                          {
                              name: 'reminder',
                              displayName: this.$t('pages.company.my_company.tabs.reminder'),
                              icon: 'bell',
                              withPadding: true,
                              hidden: this.isUser || this.offers.length === 0,
                          },
                          {
                              name: 'chart',
                              displayName: this.$t('pages.company.my_company.tabs.chart'),
                              icon: 'chart',
                              withPadding: true,
                              hidden: this.isUser,
                          },
                      ]

                this.activeTab = this.tabs[0]?.name
                this.isReady = true
            },

            tabChanged(tab) {
                this.$router.push({ name: RoutesEnum.COMPANY.MY_COMPANY, params: { id: this.$route.params.id, tabType: tab } })
            },

            goToCreateLocationPage() {
                this.$router.push({ name: 'location.create' })
            },
            openInviteUserModal() {
                this.isInviteCompanyUserModalOpen = true
            },
            closeInviteUserModal() {
                this.isInviteCompanyUserModalOpen = false
            },
            openInactivityModal() {
                this.isAddInactivityModalOpen = true
            },
            closeInactivityModal() {
                this.isAddInactivityModalOpen = false
            },
            userInvitedSuccessfully() {
                this.closeInviteUserModal()
            },
            locationDeleted() {
                this.getCompany()
            },
            formSubmit(location) {
                this.company.locationsForCompany = [...this.company.locationsForCompany, location]
                this.isProcessingLocationForm = false
                this.$toast.success(this.$t('common.microcopy.success'))
                this.$refs.updateLocationsComponent.toggleIsCreatingLocation()
            },
            editFormSubmit({ address, location, cityId, phone, email, locationId, googleBusiness, locationType }) {
                this.isProcessingLocationForm = true
                //TODO: ADD REPEATER IF MULTIPLE NEEDED
                const emails = [{ email }]
                const telephones = [
                    {
                        telephone: phone,
                    },
                ]
                LocationsAPI.editLocationForCompany({
                    address,
                    location,
                    cityId,
                    telephones,
                    emails,
                    locationId,
                    companyId: this.company.id,
                    googleBusiness,
                    locationType,
                })
                    .then(() => {
                        this.getCompany()
                        this.isProcessingLocationForm = false
                        this.$toast.success(this.$t('common.microcopy.success'))
                        this.$refs.updateLocationsComponent.toggleIsCreatingLocation()
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                        this.isProcessingLocationForm = false
                    })
            },
            getCompany() {
                const companyId = this.$route.params.id
                CompanyAPI.getById(companyId).then((company) => {
                    this.company = company
                    this.company.description = this.company?.descriptionTextContent?.originalText
                    this.company.cityId = this.company.city.id
                })
            },
            getOffers() {
                const companyId = this.$route.params.id
                OfferAPI.getCompanyOffersById(companyId).then((offers) => {
                    this.offers = offers
                })
            },
            getCustomReminders() {
                const companyId = this.$route.params.id
                CompanyAPI.getCustomReminders(companyId).then((reminders) => {
                    this.customReminders = reminders
                })
            },
            getCompanyAvailability() {
                CompanyAPI.getCompanyAvailability(this.$route.params.id).then((CompanyAvailability) => {
                    if (!this.isUser) {
                        this.companyAvailability = CompanyAvailability
                    } else {
                        this.companyAvailability = CompanyAvailability.filter((availability) => {
                            return availability.availabilityUsers.find((user) => user.user.id === this.$auth.getUser().id)
                        })
                    }
                })
            },
            getInactiveAvailabilities(append = false) {
                this.isLoading = true
                if (this.isUser) {
                    this.getUserCompanyAvailabilityInactivity(append)
                } else {
                    this.getAdminCompanyAvailabilityInactivity(append)
                }
            },
            getAdminCompanyAvailabilityInactivity(append) {
                AvailabilityAPI.getAdminCompanyAvailabilityInactivity({
                    id: this.$route.params.id,
                    take: this.limit,
                    skip: this.offset,
                })
                    .then((res) => {
                        this.loadInactiveAvailabilities(res, append)
                    })
                    .finally(() => (this.isLoading = false))
            },
            getUserCompanyAvailabilityInactivity(append) {
                AvailabilityAPI.getUserCompanyAvailabilityInactivity({
                    id: this.$route.params.id,
                    take: this.limit,
                    skip: this.offset,
                })
                    .then((res) => {
                        this.loadInactiveAvailabilities(res, append)
                    })
                    .finally(() => (this.isLoading = false))
            },
            loadMore() {
                this.offset += this.limit
                this.getInactiveAvailabilities(true)
            },
            loadInactiveAvailabilities(res, append) {
                this.isLoading = true
                this.totalCount += this.offset
                if (append) {
                    this.inactiveAvailabilities = [...this.inactiveAvailabilities, ...res]
                    return
                }
                this.inactiveAvailabilities = res
            },
            outOfAvilabilityEvent(event) {
                Store.dispatch('configuration/setIsLoading', true)

                this.dateFrom = event.dateFrom
                this.dateTo = event.dateTo
                this.availabilityIds = event.availabilityIds

                OfferAPI.getAffectedAvailabilityEvents({
                    availabilityIds: this.availabilityIds,
                    dateFrom: this.dateFrom,
                    dateTo: this.dateTo,
                }).then((res) => {
                    if (!res.outOfTimeOffers.length) {
                        this.submitInactivityFormData()
                    } else {
                        this.offersOutOfAvailability = res.outOfTimeOffers
                        this.showOutOfAvailabilityModal()
                    }
                    Store.dispatch('configuration/setIsLoading', false)
                })
            },
            submitInactivityFormData() {
                AvailabilityAPI.setupInactivity({
                    dateFrom: this.dateFrom,
                    dateTo: this.dateTo,
                    availabilityIds: this.availabilityIds,
                })
                    .then(() => {
                        this.$toast.success(this.$t('common.microcopy.success'))
                        this.closeInactivityModal()
                        this.closeOutOfAvailabilityModal()
                        this.getInactiveAvailabilities()
                        this.$auth.reloadUser()
                    })
                    .catch(() => {})
            },
            showOutOfAvailabilityModal() {
                this.isOutOfAvailabilityModalOpen = true
            },

            closeOutOfAvailabilityModal() {
                this.isOutOfAvailabilityModalOpen = false
            },
            companyRoleChanged(event, userId) {
                this.company.companyUsers.find((companyUser) => companyUser.user.id === userId).companyRole = event.companyRole
                this.$toast.success(this.$t('common.microcopy.success'))
            },
            companyUserDeleted(userId) {
                this.company.companyUsers = this.company.companyUsers.filter((companyUser) => companyUser.user.id !== userId)
                this.$toast.success(this.$t('common.microcopy.success'))
            },
            companyShowUserChanged(user, companyRole) {
                this.$startLoader()
                const request = {
                    userId: user.user.id,
                    companyRoleEnum: companyRole,
                    companyId: this.company.id,
                    showOnPublicProfile: !user.showOnPublicProfile,
                    showPublicProfile: user.showPublicProfile,
                }
                UsersAPI.updateCompanyRole(request)
                    .then(() => {
                        user.showOnPublicProfile = !user.showOnPublicProfile
                        this.$toast.success(this.$t('common.microcopy.success'))
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
                    .finally(() => this.$stopLoader())
            },

            companyShowPublicUserChanged(user, companyRole) {
                this.$startLoader()
                const request = {
                    userId: user.user.id,
                    companyRoleEnum: companyRole,
                    companyId: this.company.id,
                    showOnPublicProfile: user.showOnPublicProfile,
                    showPublicProfile: !user.showPublicProfile,
                }
                UsersAPI.updateCompanyRole(request)
                    .then(() => {
                        user.showPublicProfile = !user.showPublicProfile
                        this.$toast.success(this.$t('common.microcopy.success'))
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
                    .finally(() => this.$stopLoader())
            },

            deleteOfferById(offerId) {
                this.offers = this.offers.filter((o) => o.id !== offerId)
            },
            offerDeleted(event) {
                this.deleteOfferById(event.offerId)
                this.$toast.success(this.$t('common.microcopy.success'))
            },
            companyPreview() {
                this.$router.push({
                    name: RoutesEnum.COMPANY.PROFILE,
                    params: { id: this.$route.params.id, companyType: 'profile' },
                })
            },
            uploadPhotosApi(files) {
                const shouldUploadMainPhoto = !this.company.photos?.length
                for (let i = 0; i < files.length; i++) {
                    if (i === 0 && shouldUploadMainPhoto) {
                        files[i].type = PhotoTypeEnum.COMPANY_COVER
                    } else {
                        files[i].type = PhotoTypeEnum.COMPANY_OTHER
                    }
                }
                return CompanyAPI.uploadPhotos({
                    id: this.company.id,
                    files,
                })
            },
            removePhoto(file) {
                CompanyAPI.removePhoto(file.id).then(() => this.loadData())
            },
            setCoverPhoto(file) {
                CompanyAPI.setCoverPhoto({ companyId: this.$route.params.id, photoId: file.id }).then(() => this.loadData())
            },
            loadData() {
                return CompanyAPI.getById(this.$route.params.id).then((company) => {
                    company.photos = [
                        ...company.photos.filter((photo) => photo.type === PhotoTypeEnum.COMPANY_MAIN),
                        ...company.photos.filter((photo) => photo.type !== PhotoTypeEnum.COMPANY_MAIN),
                    ]
                    this.company = company
                    this.company.cityId = this.company.city.id
                })
            },

            openLocationTab() {
                this.isLocationYesNoModalOpen = false
                this.$refs.tabs.tabClicked('locations')
                this.isSelectLocationModalOpen = false
            },

            checkIfCompanyHasLocations() {
                if (!this.isLocationReminderCalled) {
                    const company = this.$auth
                        .getUser()
                        .companyUsers?.find((item) => item?.company?.id === Number(this.$route.params?.id))
                    if (!company?.company.locationsForCompany?.length && this.$route.params.tabType !== 'locations') {
                        this.yesNoHeaderText = this.$t('forms.user.profile.messages.locations_not_set')
                        this.isLocationYesNoModalOpen = true
                        this.isLocationReminderCalled = true
                    }
                }
            },

            getInvoices() {
                return CompanyAPI.getInvoices(this.$route.params.id).then((invoices) => {
                    this.invoices = invoices
                })
            },

            toggleSelectLocationModal() {
                this.isSelectLocationModalOpen = !this.isSelectLocationModalOpen
            },
            createOffer(location) {
                this.$router.push({
                    name: RoutesEnum.OFFER.CREATE,
                    query: { location: location.id, company: this.$route.params.id },
                })
            },
            companySettingsUpdated() {
                this.getInvoices()
            },

            toggleCompanyStatusModal() {
                this.isChangeCompanyStatusModalOpen = !this.isChangeCompanyStatusModalOpen
            },

            changeCompanyStatus() {
                if (!this.company.deletedAt) {
                    return CompanyAPI.deleteCompany(this.company.id)
                        .then(() => {
                            this.$toast.success(this.$t('pages.company.my_company.successfully_deleted'))
                            this.$auth.reloadUser().then(() => {
                                this.company.deletedAt = true
                                this.getTabs()
                                this.toggleCompanyStatusModal()
                            })
                        })
                        .catch(() => {
                            this.$toast.error(this.$t('common.errors.common_error'))
                            this.toggleCompanyStatusModal()
                        })
                }

                return CompanyAPI.activateCompany(this.company.id)
                    .then(() => {
                        this.$toast.success(this.$t('pages.company.my_company.successfully_reactivated'))
                        this.$auth.reloadUser().then(() => {
                            this.company.deletedAt = null
                            this.getTabs()
                            this.toggleCompanyStatusModal()
                        })
                    })
                    .catch((e) => {
                        const errorMessage = e.error.message
                        switch (errorMessage) {
                            case ErrorsEnum.COMPANY.NOT_FOUND:
                                this.$toast.error(this.$t('pages.company.my_company.errors.not_found'))
                                break
                            case ErrorsEnum.COMPANY.ALREADY_ACTIVE:
                                this.$toast.error(this.$t('pages.company.my_company.errors.already_active'))
                                break
                            case ErrorsEnum.COMPANY.IS_IN_DEBT:
                                this.$toast.error(this.$t('pages.company.my_company.errors.is_in_debt'))
                                break
                            case 'USER.NOT_FOUND':
                                this.$router.push({ name: RoutesEnum.ERROR.NO_ACCESS })
                                break
                            default:
                                this.$toast.error(this.$t('common.errors.common_error'))
                                break
                        }
                        this.toggleCompanyStatusModal()
                    })
            },

            currentCompany() {
                return this.$auth.getUser()?.companyUsers?.find((c) => c.company.id == this.$route.params?.id)?.company
            },

            getChartRevenueData(query) {
                CompanyAPI.getChartRevenueData(this.$route.params.id, query).then((response) => {
                    const yearData = response[0]

                    this.chartData = {
                        labels: yearData.months.map((monthData) => {
                            return this.$t(`common.months.${monthData.month - 1}.short`)
                        }),
                        datasets: [
                            {
                                label: '',
                                data: yearData.months.map((monthData) => this.setPrice(monthData.revenue)),
                                backgroundColor: ['#55379d'],
                            },
                        ],
                    }
                    let total = `${this.setPrice(response[0].total)} ${query.currency}`
                    this.totalSumLabel = `${this.$t(`forms.chart.chart_filter.total.revenue`)} ${total}`
                })
            },
            getCustomReminderStatistic(query) {
                CompanyAPI.getCustomReminderStatistic(this.$route.params.id, query)
                    .then(async (response) => {
                        const yearData = response[0]
                        const currency = this.showPriceRSD ? CurrencyEnum.RSD : CurrencyEnum.BAM
                        const totalSmsLabel = this.isStatisticCustomReservationPage
                            ? `${this.$t('pages.company.custom_reminders.totalSmsPrice')} ${currency}`
                            : this.$t('pages.company.custom_reminders.totalSmsCount')
                        const totalEmailLabel = this.isStatisticCustomReservationPage
                            ? `${this.$t('pages.company.custom_reminders.totalEmailPrice')} ${currency}`
                            : this.$t('pages.company.custom_reminders.totalEmailCount')

                        this.chartData = {
                            labels: yearData.months.map((monthData) => {
                                return this.$t(`common.months.${monthData.month - 1}.short`)
                            }),
                            datasets: [
                                {
                                    label: totalSmsLabel,
                                    data: yearData.months.map((monthData) =>
                                        this.isStatisticCustomReservationPage ? monthData.smsPrice : monthData.smsCount,
                                    ),
                                    backgroundColor: '#55379d',
                                    fill: false,
                                },
                                {
                                    label: totalEmailLabel,
                                    data: yearData.months.map((monthData) =>
                                        this.isStatisticCustomReservationPage ? monthData.emailPrice : monthData.emailCount,
                                    ),
                                    backgroundColor: '#36a2eb',
                                    fill: false,
                                },
                            ],
                        }
                        this.totalSumLabel =
                            this.showPriceRSD && this.isStatisticCustomReservationPage
                                ? `* ${this.$t('pages.company.custom_reminders.pdv_message')}`
                                : undefined
                    })
                    .catch(() => {
                        this.$toast.error(this.$t('common.errors.common_error'))
                    })
            },
            getChartOfferData(query) {
                if (this.$route.query.client) {
                    const chosenClient = JSON.parse(this.$route.query.client)
                    query.initiatorId = chosenClient.id
                }

                CompanyAPI.getChartOfferData(this.$route.params.id, query).then((response) => {
                    const yearData = response[0]

                    this.chartData = {
                        labels: yearData.months.map((monthData) => {
                            return this.$t(`common.months.${monthData.month - 1}.short`)
                        }),
                        datasets: [
                            {
                                label: '',
                                data: yearData.months.map((monthData) => monthData.numberOfCalendarEvents),
                                backgroundColor: ['#55379d'],
                            },
                        ],
                    }

                    this.totalSumLabel = `${this.$t(`forms.chart.chart_filter.total.offer`)} ${response[0].total} `
                })
            },

            chartFilterChanged(payload) {
                if (!payload.dateFrom && !payload.dateTo) {
                    payload.dateFrom = dayjs().year(this.currentYear).startOf('year').format('YYYY-MM-DD')
                    payload.dateTo = dayjs().year(this.currentYear).endOf('year').format('YYYY-MM-DD')
                }
                this.statisticOption = payload.statisticOption
                delete payload.statisticOption

                if (
                    this.statisticOption === StatisticOptionsEnum.CUSTOM_RESERVATION_SEND ||
                    this.statisticOption === StatisticOptionsEnum.CUSTOM_RESERVATION_PRICE
                ) {
                    payload.currency = this.showPriceRSD ? CurrencyEnum.RSD : CurrencyEnum.BAM
                    this.getCustomReminderStatistic(payload)
                } else if (this.statisticOption === StatisticOptionsEnum.OFFER) {
                    this.getChartOfferData(payload)
                } else {
                    this.getChartRevenueData(payload)
                }
            },

            reminderChanged() {
                this.getCustomReminders()
            },

            getChartQuery() {
                this.chartQuery.dateFrom = dayjs().year(this.currentYear).startOf('year').format('YYYY-MM-DD')
                this.chartQuery.dateTo = dayjs().year(this.currentYear).endOf('year').format('YYYY-MM-DD')
                this.chartQuery.status = this.statusOptions
                    .filter((item) =>
                        [
                            ReservationStatusFilterEnum.DONE.value,
                            ReservationStatusFilterEnum.ACCEPTED.value,
                            ReservationStatusFilterEnum.OPEN.value,
                        ].includes(item.value),
                    )
                    .map((item) => item.value)
                if (this.$i18n.locale === LanguageEnum.SR) {
                    this.currency = CurrencyEnum.RSD.value
                } else if (this.$i18n.locale === LanguageEnum.BIH) {
                    this.currency = CurrencyEnum.BAM.value
                } else {
                    this.currency = CurrencyEnum.EUR.value
                }
            },
        },

        watch: {
            '$route.params.id': {
                handler: function () {
                    this.isReady = false
                    this.activeTab = null
                    this.userCompanyRole = this.$auth
                        .getUser()
                        .companyUsers?.find((item) => item.company?.id === Number(this.$route.params?.id))?.companyRole
                    this.getTabs()
                    this.checkIfCompanyHasLocations()
                    if (this.$route.params?.id && this.$route.name === RoutesEnum.COMPANY.MY_COMPANY) {
                        this.getCompany()
                        if (!this.isUser) {
                            this.getInvoices()
                        }
                        if (this.isAdmin) {
                            this.getCustomReminders()
                        }
                        if (!this.currentCompany().deletedAt) {
                            this.getOffers()
                            this.getInactiveAvailabilities()
                            this.getCompanyAvailability()
                        } else {
                            this.$refs.tabs?.tabClicked(this.tabs[0].name)
                            this.offers = []
                        }
                    }
                },
                deep: true,
                immediate: true,
            },
            '$route.params.tabType': {
                handler: function () {
                    if (this.$route.params.id && this.$route.params.tabType) {
                        this.$refs.tabs.tabClicked(this.$route.params.tabType)
                    }

                    if (this.$route.params.tabType === 'chart') {
                        this.getChartQuery()
                        if (this.isAdmin) {
                            this.getChartOfferData(this.chartQuery)
                        }
                    }
                },
            },
            '$i18n.locale': {
                handler: function () {
                    this.getTabs()
                },
            },

            offers: {
                handler: function () {
                    this.getTabs()
                },
            },
        },

        created() {
            this.getChartQuery()

            if (this.isAdmin) {
                this.getChartOfferData(this.chartQuery)
            }
        },

        mounted() {
            this.userCompanyRole = this.$auth
                .getUser()
                .companyUsers?.find((item) => item.company.id === Number(this.$route.params.id))?.companyRole
            if (!this.userCompanyRole) {
                this.$router.push({ name: RoutesEnum.ERROR.NO_ACCESS })
            }
            this.getTabs()
            if (this.$route.params.tabType) {
                this.$refs.tabs.tabClicked(this.$route.params.tabType)
            }
            this.checkIfCompanyHasLocations()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';

    .inactivity-modal {
        height: 50%;
    }
    .company-users-grid {
        grid-gap: 15px !important;
    }

    .my-company {
        &__buttons-activate {
            padding-bottom: 10px;

            @include breakpoint(tablet-small) {
                padding-bottom: 0;
            }

            &__button {
                background: $color-bright-red;

                @include breakpoint(tablet-small) {
                    margin-bottom: 10px;
                }

                &:hover,
                &:focus {
                    background: $color-bright-red !important;
                    opacity: 0.8;
                }
            }
        }

        &__deactivate {
            position: absolute;
            bottom: 10px;
            left: 10px;
            font-size: $font-size--form;
            color: $subdomain-light-grey;

            @include breakpoint(tablet-small) {
                bottom: 0px;
                left: 5px;
                font-size: $font-size--secondary;
            }
            &:hover {
                cursor: pointer;
            }
        }
    }
</style>
